import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as problemService from "../services/problemService";
import { trackFirstDailyGoal, trackSecondDayStreak } from "../api/tiktokEvents";

// Backend przydziela 2 backupy na zadanie bazowe — kolejka nie może dosypać więcej.
const MAX_BACKUPS_PER_PROBLEM = 2;

// Backupy dokładamy WYŁĄCZNIE po pomyłce na zadaniu bazowym.
// Bez tego błąd na zadaniu naprawczym generował kolejne naprawcze w nieskończoność
// (subfield ma zwykle więcej niż 2 backupy — należą do innych grup bazowych).
// `origin` musi się zgadzać, żeby zadanie doklejone przed sesją (pewniak /
// niepewniak) nie pociągnęło backupów z głównej puli o zbieżnym subfieldzie.
const findBackupsFor = (baseProblem, allProblems, queue) => {
    if (baseProblem.problem_type !== 'base') return [];
    // Na rozszerzeniu jednostką doboru jest szablon, nie podtemat — jeden podtemat
    // ma tam po kilka szablonów, więc dopasowanie po subfield_id wciągnęłoby
    // naprawcze z sąsiedniego szablonu. Gdy backend poda template_id, ma pierwszeństwo.
    const matchesGroup = baseProblem.template_id
        ? (p) => p.template_id === baseProblem.template_id
        : (p) => p.subfield_id === baseProblem.subfield_id;
    return allProblems
        .filter(p => p.problem_type === 'backup'
            && matchesGroup(p)
            && p.origin === baseProblem.origin)
        .filter(b => !queue.some(p => p.problem_id === b.problem_id))
        .slice(0, MAX_BACKUPS_PER_PROBLEM);
};

// Kolejka = zadania bazowe + backupy doklejone po każdej pomyłce z dotychczasowej sesji.
const buildQueue = (allProblems, session) => {
    const queue = allProblems.filter(p => p.problem_type === 'base');
    session.forEach((solvedProblem) => {
        if (solvedProblem.is_solved) return;
        const idx = queue.findIndex(p => p.problem_id === solvedProblem.problem_id);
        if (idx > -1) {
            const backups = findBackupsFor(queue[idx], allProblems, queue);
            if (backups.length > 0) queue.splice(idx + 1, 0, ...backups);
        }
    });
    return queue;
};

const readJson = (key) => {
    try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
};

const buildAnswers = (problem) => [
    { text: problem.true_answer, isCorrect: true },
    { text: problem.false_answer1, isCorrect: false },
    { text: problem.false_answer2, isCorrect: false },
    { text: problem.false_answer3, isCorrect: false },
].filter(ans => ans.text != null && ans.text !== '').sort(() => Math.random() - 0.5);

const readFieldElo = (problem) => readJson("userEloDict")?.[problem.field_id] || 0;

// Funkcja pomocnicza poza hookiem
const getInitialUserStatus = () => {
    const status = JSON.parse(localStorage.getItem("userStatus"));
    return {
        solvedToday: status?.problems_solved_today ?? 0,
        dailyGoal: status?.problems_daily_goal ?? 5,
        currentStreak: status?.current_streak ?? 0,
        totalSolved: status?.problems_solved_total ?? 0, 
    };
};

export const useProblemSolver = ({ isSurefireMode = false } = {}) => {
    const navigate = useNavigate();

    // --- Stany ---
    // Cała sesja (z backupami) i odpowiedzi liczone synchronicznie z localStorage: pierwsza
    // klatka musi być kompletna. Wcześniej rysowało się zadanie bez odpowiedzi, z ELO 0,
    // a przy wznowionej sesji czasem inne zadanie — i dopiero efekt to podmieniał (miganie).
    const [initial] = useState(() => {
        const all = readJson("problemsData") || [];
        const session = readJson("problemsSession") || [];
        const queue = buildQueue(all, session);
        const problem = queue[session.length];
        return {
            all, session, queue,
            answers: problem ? buildAnswers(problem) : [],
            elo: problem ? readFieldElo(problem) : 0,
        };
    });
    const [problemsSession, setProblemsSession] = useState(initial.session);
    const [allProblems, setAllProblems] = useState(initial.all);
    const [problemQueue, setProblemQueue] = useState(initial.queue);
    const [currentIndex, setCurrentIndex] = useState(initial.session.length);

    const [wasLastAnswerCorrect, setWasLastAnswerCorrect] = useState(null);
    const [isBackupModeTriggered, setIsBackupModeTriggered] = useState(false);
    const [answers, setAnswers] = useState(initial.answers);
    const [selectedAnswerText, setSelectedAnswerText] = useState(null);
    const [isAnswerChecked, setIsAnswerChecked] = useState(false);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
    const [showSolution, setShowSolution] = useState(false);
    const [isHintUsed, setIsHintUsed] = useState(false);
    const [isSolutionViewed, setIsSolutionViewed] = useState(false);
    const [userFieldElo, setUserFieldElo] = useState(initial.elo);
    const [eloChange, setEloChange] = useState(null);
    const [lastSubmissionResult, setLastSubmissionResult] = useState(null); 

    // =========================================================================
    // NOWY STATE: sygnał "API odpowiedziało, wszystkie dane są aktualne"
    // Popup w SolveProblem reaguje na TEN state zamiast na isAnswerChecked,
    // żeby mieć gwarancję, że currentStreak, solvedToday itp. są świeże.
    // =========================================================================
    const [attemptProcessed, setAttemptProcessed] = useState(false);

    // Stany usera
    const [solvedToday, setSolvedToday] = useState(getInitialUserStatus().solvedToday);
    const [dailyGoal, setDailyGoal] = useState(getInitialUserStatus().dailyGoal);
    const [currentStreak, setCurrentStreak] = useState(getInitialUserStatus().currentStreak);
    const [totalSolved, setTotalSolved] = useState(getInitialUserStatus().totalSolved); 

    const [showConfetti, setShowConfetti] = useState(false);
    const [startTime, setStartTime] = useState(() => initial.answers.length > 0 ? Math.floor(Date.now() / 1000) : null);
    const [isSessionInitialized, setIsSessionInitialized] = useState(false);
    const [reinitKey, setReinitKey] = useState(0);
    const reinitialize = useCallback(() => setReinitKey(k => k + 1), []);
    
    const answersLoaded = useRef(initial.answers.length > 0);
    const [shouldRequestReview, setShouldRequestReview] = useState(false);

    // --- Zmienne ---
    const currentProblem = problemQueue[currentIndex];
    const problemNumber = problemsSession.length + 1;

    // --- Funkcje pomocnicze ---
    const resetForNewProblem = useCallback(() => {
        answersLoaded.current = false;
        setIsHintUsed(false);
        setIsSolutionViewed(false);
        setIsAnswerChecked(false);
        setSelectedAnswerText(null);
        setIsSubmitDisabled(false);
        setShowSolution(false);
        setEloChange(null); 
        setWasLastAnswerCorrect(null);
        setIsBackupModeTriggered(false);
        setShouldRequestReview(false);
        setLastSubmissionResult(null);
        setAttemptProcessed(false); // ← reset sygnału
    }, []);

    const clearProblemSession = useCallback(() => {
        ["problemsSession", "userEloDict", "problemsData", "solveProblemState", "sessionStartTime", "fullUserStatsBeforeSession","userStatusBeforeSession", "available_fields"].forEach(key => localStorage.removeItem(key));
        setAllProblems([]);
        setProblemQueue([]);
        setCurrentIndex(0);
        setProblemsSession([]);
        setIsSessionInitialized(false);
    }, []);

    // --- Efekty ---
    
    useEffect(() => {
        const allProblemsFromStorage = readJson("problemsData");
        if (!allProblemsFromStorage || allProblemsFromStorage.length === 0) {
            navigate("/", { replace: true });
            return;
        }

        // Pierwszy montaż ma już stan z inicjalizatora; przebudowa tylko po reinitialize
        // (stan React może być wtedy stary, więc czytamy z localStorage).
        if (reinitKey > 0) {
            const existingSession = readJson("problemsSession") || [];
            setProblemsSession(existingSession);
            resetForNewProblem();
            setAllProblems(allProblemsFromStorage);
            setProblemQueue(buildQueue(allProblemsFromStorage, existingSession));
            setCurrentIndex(existingSession.length);
        }

        setIsSessionInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, reinitKey]);

    // Layout effect: nowe odpowiedzi i ELO trafiają na ekran w tej samej klatce co zadanie.
    useLayoutEffect(() => {
        if (currentProblem && !answersLoaded.current) {
            setStartTime(Math.floor(Date.now() / 1000));
            setAnswers(buildAnswers(currentProblem));
            answersLoaded.current = true;
            setUserFieldElo(readFieldElo(currentProblem));
        }
    }, [currentProblem]);

    useEffect(() => {
        if (problemsSession.length > 0) {
            localStorage.setItem("problemsSession", JSON.stringify(problemsSession));
        }
    }, [problemsSession]);

    // --- Handlery ---
    const handleNextProblem = useCallback(async () => {
        const lastProblem = currentProblem;
        if (!lastProblem || wasLastAnswerCorrect === null) {
            return true;
        }

        const isCorrect = wasLastAnswerCorrect;
        let updatedQueue = [...problemQueue];

        if (!isCorrect) {
            const backupsToInsert = findBackupsFor(lastProblem, allProblems, updatedQueue);

            if (backupsToInsert.length > 0) {
                // Reality check istnieje tylko dla zadań zamkniętych (klik "ROZWIĄZANIE" bez próby).
                // Otwarte mają samoocenę zamiast wyboru odpowiedzi → zawsze comeback.
                const isOpen = !lastProblem.false_answer1 && !lastProblem.false_answer2 && !lastProblem.false_answer3;
                const originTag = (!isOpen && !selectedAnswerText) ? 'solution' : 'error';

                const taggedBackups = backupsToInsert.map(p => ({
                    ...p,
                    origin: originTag
                }));

                updatedQueue.splice(currentIndex + 1, 0, ...taggedBackups);
                setProblemQueue(updatedQueue);
            }
        }

        const nextIndex = currentIndex + 1;

        if (nextIndex < updatedQueue.length) {
            setCurrentIndex(nextIndex);
            resetForNewProblem();
            return false;
        }

        if (isSurefireMode) return true;

        try {
            const data = await problemService.fetchNextProblems();
            if (data.final_problems && data.final_problems.length > 0) {
                const newProblems = data.final_problems;
                const newBaseProblems = newProblems.filter(p => p.problem_type === 'base');
                const combinedProblems = [...allProblems, ...newProblems];
                setAllProblems(combinedProblems);
                localStorage.setItem("problemsData", JSON.stringify(combinedProblems));
                localStorage.setItem("userEloDict", JSON.stringify(data.user_elo_dict));
                setProblemQueue(prevQueue => [...prevQueue, ...newBaseProblems]);
                setCurrentIndex(nextIndex);
                resetForNewProblem();
                
                return false;
            } else {
                return true;
            }
        } catch (error) {
            console.error("Błąd pobierania nowych zadań:", error);
            return true;
        }
    }, [currentIndex, problemQueue, allProblems, currentProblem, wasLastAnswerCorrect, resetForNewProblem, selectedAnswerText]);

    const processAttempt = useCallback(async (isCorrect, isSolutionClick = false) => {
        setWasLastAnswerCorrect(isCorrect);
        
        if ((!isCorrect || isSolutionClick) && currentProblem?.problem_type === 'base') {
            setIsBackupModeTriggered(true);
        }
    
        if (!currentProblem) return;

        const currentEndTime = Math.floor(Date.now() / 1000);
        
        // Te stany ustawiamy PRZED await — natychmiastowy feedback UI
        // (blokada przycisków, kolorowanie odpowiedzi, pokazanie rozwiązania)
        setIsAnswerChecked(true);
        setIsSubmitDisabled(true);

        if (!isCorrect || isSolutionClick) {
            setShowSolution(true);
            setIsSolutionViewed(true);
        }

        const problemAttemptData = {
            problem_id: currentProblem.problem_id,
            field_id: currentProblem.field_id,
            is_solved: isCorrect,
            is_hint_used: isHintUsed,
            is_solution_viewed: isSolutionViewed || isSolutionClick,
            start_time: startTime,
            end_time: currentEndTime,
        };

        try {
            const data = await problemService.submitProblemAttempt(problemAttemptData);
            
            setLastSubmissionResult(data); 
            setEloChange(data.attempt_elo_change);
            setSolvedToday(data.problems_solved_today);
            setDailyGoal(data.problems_daily_goal);
            setCurrentStreak(data.current_streak ?? currentStreak);

            const currentStoredStatus = JSON.parse(localStorage.getItem("userStatus")) || {};
            const previousTotal = currentStoredStatus.problems_solved_total || totalSolved; 
            const newTotalSolved = isCorrect ? previousTotal + 1 : previousTotal;
            setTotalSolved(newTotalSolved);

            if (data.new_badge_achieved) {
                localStorage.setItem('newBadgeToShow', JSON.stringify(data.new_badge_achieved));
            }

            if (data.request_review_trigger) {
                setShouldRequestReview(true);
            }
            
            localStorage.setItem("userStatus", JSON.stringify({
                problems_solved_today: data.problems_solved_today,
                problems_daily_goal: data.problems_daily_goal,
                current_streak: data.current_streak ?? currentStreak,
                problems_solved_total: newTotalSolved,
                promo_offer_expires_at: data.promo_offer_expires_at
            }));
            
            if (isCorrect && data.daily_goal_completed) {
                setShowConfetti(true);
                setTimeout(() => setShowConfetti(false), 5000);

                // Backend zwraca streak juz po inkrementacji, a daily_goal_completed leci
                // dokladnie raz na dobe — 1 to pierwszy cel w zyciu, 2 to powrot nazajutrz.
                const userId = localStorage.getItem("user_id");
                if (data.current_streak === 1) {
                    trackFirstDailyGoal(userId);
                } else if (data.current_streak === 2) {
                    trackSecondDayStreak(userId);
                }
            }
            
            setProblemsSession(prev => [
                ...prev, {
                    ...problemAttemptData,
                    user_elo: Math.max(0, userFieldElo + data.attempt_elo_change),
                    // ELO zadania z momentu proby — to informacja o trudnosci, z ktora
                    // user sie zmierzyl. Rating zadania dryfuje przez innych userow i
                    // jego to nie dotyczy, wiec nie doliczamy tu zadnej delty.
                    problem_elo: currentProblem.problem_elo,
                    elo_change: data.attempt_elo_change,
                    question: currentProblem.question,
                    points_assigned: currentProblem.points_assigned,
                    solution: currentProblem.solution,
                    image_link: currentProblem.image_link,
                    hint: currentProblem.hint,
                    linked_formulas: currentProblem.linked_formulas,
                    linked_matura_problems: currentProblem.linked_matura_problems,
                    answers: answers,
                    selected_answer: selectedAnswerText,
                    subfield_id: currentProblem.subfield_id 
                },
            ]);

            if (data.user_elo_dict) {
                localStorage.setItem("userEloDict", JSON.stringify(data.user_elo_dict));
            }

            // =========================================================
            // SYGNAŁ: "API odpowiedziało, dane są aktualne"
            // Popup useEffect w SolveProblem czeka na TEN state.
            // Ustawiamy go OSTATNI, żeby React zbatchował go razem
            // z solvedToday, currentStreak, shouldRequestReview itd.
            // =========================================================
            setAttemptProcessed(true);

        } catch (error) {
            console.error("Błąd podczas wysyłania danych próby:", error);
        }
    }, [currentProblem, isHintUsed, isSolutionViewed, startTime, answers, userFieldElo, selectedAnswerText, totalSolved, currentStreak]);
    
    const handleAnswerClick = useCallback((answer) => {
        if (!isAnswerChecked) {
            setSelectedAnswerText(answer.text);
            processAttempt(answer.isCorrect);
        }
    }, [isAnswerChecked, processAttempt]);

    const handleSolution = useCallback(() => {
        if (!isAnswerChecked) {
            processAttempt(false, true);
        }
    }, [isAnswerChecked, processAttempt]);

    const handleHint = useCallback(() => setIsHintUsed(true), []);
    
    const handleEndSession = useCallback(() => {
        navigate("/end-session-stats", { replace: true });
    }, [navigate]);

    return {
        currentProblem, problemsSession, answers, selectedAnswerText,
        wasLastAnswerCorrect,
        isAnswerChecked, isSubmitDisabled, showSolution, isHintUsed,
        userFieldElo, eloChange, problemNumber, solvedToday, dailyGoal,
        currentStreak, showConfetti, totalSolved,
        handleAnswerClick, handleSolution, handleHint, handleNextProblem, handleEndSession,
        setShowSolution,
        isSessionInitialized,
        clearProblemSession,
        isBackupModeTriggered,
        shouldRequestReview,
        lastSubmissionResult,
        attemptProcessed,
        reinitialize,
    };
};